import React from 'react';
import uniqBy from 'lodash/uniqBy';

import { ChartConsumer } from '@ihme/common/web/components/ChartProvider';
import { LineChart } from '@ihme/common/web/components';
import _  from '@ihme/common/locale';

import config from '../../../config';

import { renderChartCopyright } from '../../../utility/chart-copyright';
import formatChartValue from '../../../utility/formatting/formatChartValue';
import formatChartLabel from '../../../utility/formatting/formatChartLabel';

import echartsTheme from '../../../theme/echarts';
import { pngIcon } from '../../../theme/icons';

type Props = {
    locationId: number;
};

class DeathsChart extends React.Component<Props> {
    renderTooltip = (params, { isMobileView }) => {
        const { columns } = this.props;
        const valueIdx = columns.indexOf('value');
        const lowerIdx = columns.indexOf('lower');
        const upperIdx = columns.indexOf('upper');
        const locationIdx = columns.indexOf('location_id');
        const measureIdx = columns.indexOf('measure_id');
        const header = params[0].axisValue;

        const sortedLines = uniqBy(
            params.filter(lineData => lineData.data[valueIdx] !== undefined && lineData.data[valueIdx] !== null)
        ).sort((a, b) => b.data[valueIdx] - a.data[valueIdx]);

        const parseLineValue = lineParams => {
            const { data } = lineParams;

            const getMarker = color =>
                `<span
                    style='
                        display:inline-block;
                        margin-right:8px;
                        border-radius:10px;
                        width:10px;
                        height:10px;
                        background-color:${color};'>
                </span>`;

            const color =
                typeof lineParams.color === 'object'
                    ? lineParams.color.colorStops[0].color
                    : lineParams.color;

            const upper = formatChartValue(data[upperIdx]);
            const lower = formatChartValue(data[lowerIdx]);

            const uncertainty = upper !== null && lower !== null ? `(${lower} - ${upper})` : '';
            const value = `${formatChartValue(data[valueIdx])} ${uncertainty}`;

            return `<div class='chart-tooltip-text'>
                    ${getMarker(color)}
                    <span class='chart-tooltip-text-bold'>
                        ${_(`location_${data[locationIdx]}`)}:${isMobileView ? `<br/>` : ' '}${value}
                    </span>
                    ${' ' + _(`tooltip_measure_${data[measureIdx]}`)}
                </div>`;
        };

        return `<div class='chart-tooltip-title'>${header}</div>
               ${sortedLines.map(parseLineValue).join('')}`;
    };

    getSaveFilename = () => _('factsheets_deaths_chart_title');

    render() {
        const { columns } = this.props;
        const locationIdx = columns.indexOf('location_id');

        return (
            <LineChart
                styles={{
                    paddingTop: 0,
                    marginBottom: 0,
                }}
                theme={{
                    ...echartsTheme,
                    toolbox: {
                        top: 50,
                        right: 26,
                        itemSize: 26,
                        itemGap: 20,
                        iconStyle: {
                            color: 'rgba(0, 0, 0, .6)',
                            borderWidth: 0,
                            emphasis: {
                                color: '#0094C9',
                            },
                        },
                    },
                    line: {
                        grid: {
                            top: 100,
                            left: 70,
                        },
                        legend: {
                            show: true,
                            left: 'center',
                            top: 72,
                            icon: 'circle',
                        },
                    }
                }}
                formatSeriesName={(groupIdx, key, columns, values: any) => _(`location_${values[locationIdx]}`)}
                generateLineParams={(values: any) => ({
                    color: echartsTheme.line.color[
                        values[locationIdx] === config.globalLocationId ? 0 : 1
                    ],
                })}
                withSlider
                groupByKeys={['location_id']}
                yAxisLabelFormatter={formatChartLabel}
                renderYAxisTitle={({ filters: { metric_id, measure_id } }) =>
                    _(`measure_${measure_id}_full_name`) + ', ' + _(`metric_${metric_id}`)
                }
                renderTooltip={this.renderTooltip}
                renderTitle={() => _('factsheets_deaths_chart_title')}
                renderSubtitle={() => _('factsheets_deaths_chart_subtitle')}
                renderGraphic={renderProps => renderChartCopyright(renderProps, 76, 50)}
                configureGrid={({ isMobileView }) => ({
                    bottom: isMobileView ? 130 : 120,
                })}
                saveAsImage={{
                    visible: true,
                    filename: this.getSaveFilename,
                    icon: pngIcon,
                }}
            />
        );
    }
}

export default (props) => (
    <ChartConsumer>{(consumerProps) => <DeathsChart {...consumerProps} {...props} />}</ChartConsumer>
);
